/**
 * Gifting & deprived assets for Age Pension-style means tests.
 * Ported verbatim from frontend/src/calculations/giftingDeprivation.js.
 *
 * Uses Australian financial years ending 30 June. Legacy/UI keys `yearYYYY` in `giftsHistory` mean:
 * total gifts in the FY 1 July (YYYY−1) – 30 June YYYY.
 */
import { GIFTING_LIMIT_ANNUAL, GIFTING_LIMIT_5_YEAR } from './constants/ratesAndThresholds.ts';
import type { FactFindGiftsHistory } from './factFindTypes.ts';
import type { StrategyOptions } from './strategyOptionsTypes.ts';

/** Australian FY end year containing `date` (FY is 1 July (E−1) … 30 June E). */
export function getAustralianFYEndingYearContainingDate(date: Date | string): number {
    const d = date instanceof Date ? date : new Date(date);
    const y = d.getFullYear();
    const m = d.getMonth(); // 0 = Jan
    return m >= 6 ? y + 1 : y;
}

/** Parse giftsHistory + optional strategy gifts into totals per FY end year. */
export function buildGiftsByFinancialYear(
    giftsHistory: Record<string, unknown> | null | undefined,
    strategyGiftsByFyEnd: Record<string, unknown> = {},
): Map<number, number> {
    const byFy = new Map<number, number>();

    const add = (fy: number, amt: unknown) => {
        const n = Math.max(0, Number(amt) || 0);
        if (n <= 0) return;
        byFy.set(fy, (byFy.get(fy) || 0) + n);
    };

    if (giftsHistory && typeof giftsHistory === 'object') {
        for (const key of Object.keys(giftsHistory)) {
            if (!key.startsWith('year')) continue;
            const fy = parseInt(key.slice(4), 10);
            if (!Number.isFinite(fy)) continue;
            add(fy, giftsHistory[key]);
        }
    }

    if (strategyGiftsByFyEnd && typeof strategyGiftsByFyEnd === 'object') {
        for (const [k, v] of Object.entries(strategyGiftsByFyEnd)) {
            const fy = parseInt(k, 10);
            if (Number.isFinite(fy)) add(fy, v);
        }
    }

    return byFy;
}

/** Total deprived assets included in assessable assets / deeming for the income test snapshot. */
export function calculateDeprivedAssetsFromGifts(
    giftsHistory: Record<string, unknown> | null | undefined,
    options: {
        asOfDate?: Date;
        strategyGiftsByFyEnd?: Record<string, unknown>;
        annualLimit?: number;
        fiveYearLimit?: number;
    } = {},
): number {
    const {
        asOfDate = new Date(),
        strategyGiftsByFyEnd = {},
        annualLimit = GIFTING_LIMIT_ANNUAL,
        fiveYearLimit = GIFTING_LIMIT_5_YEAR,
    } = options;

    const hasHistory =
        giftsHistory &&
        typeof giftsHistory === 'object' &&
        (!!(giftsHistory as any).hasGifted ||
            Object.keys(giftsHistory).some((k) => k.startsWith('year') && (Number((giftsHistory as any)[k]) || 0) > 0));

    const hasStrategy = Object.values(strategyGiftsByFyEnd).some((v) => (Number(v) || 0) > 0);

    if (!hasHistory && !hasStrategy) return 0;

    const byFy = buildGiftsByFinancialYear(giftsHistory || {}, strategyGiftsByFyEnd);
    if (byFy.size === 0) return 0;

    const currentFyEnd = getAustralianFYEndingYearContainingDate(asOfDate);
    const fyStart = currentFyEnd - 4;
    const fyEnd = currentFyEnd;

    let sumAnnualExcess = 0;
    for (let fy = fyStart; fy <= fyEnd; fy++) {
        const g = byFy.get(fy) || 0;
        sumAnnualExcess += Math.max(0, g - annualLimit);
    }

    let maxFiveYearExcess = 0;
    for (let end = fyStart + 4; end <= fyEnd; end++) {
        let windowSum = 0;
        for (let y = end - 4; y <= end; y++) {
            windowSum += byFy.get(y) || 0;
        }
        maxFiveYearExcess = Math.max(maxFiveYearExcess, Math.max(0, windowSum - fiveYearLimit));
    }

    return Math.max(sumAnnualExcess, maxFiveYearExcess);
}

/**
 * Deprived assets for Age Pension asset test / deeming (gifting free areas, Australian FY), with the
 * gifts a STRATEGY proposes folded in alongside the ones already in the fact-find.
 *
 * The thin strategy-aware wrapper over {@link calculateDeprivedAssetsFromGifts} above: it reads the
 * three places a strategy can propose a gift from (a downsize, an investment-property sale, or the
 * standalone gifting strategy), books each into the financial year `asOfDate` falls in, and hands the
 * combined picture down.
 *
 * Also called directly, outside any means test, by the UI screens that show a client what a gift they
 * are contemplating would cost them (AgedCareAnalyser, AgedCareInsightPanel, RVOtherStrategiesSection),
 * which is why it lives here beside the gifting rules rather than in deemedIncome.ts with the means
 * test that is only one of its callers.
 */
export function calculateDeprivedAssets(
    giftsHistory: FactFindGiftsHistory | undefined,
    strategyOptions: StrategyOptions = {},
    asOfDate: Date = new Date(),
    extraGiftsByFyEnd: Record<string | number, number> | null = null,
): number {
    const fyEnd = getAustralianFYEndingYearContainingDate(asOfDate);
    const strategyGiftsByFyEnd: Record<string | number, number> = {};
    const addStrategyGift = (raw: number) => {
        const n = raw || 0;
        if (n > 0) strategyGiftsByFyEnd[fyEnd] = (strategyGiftsByFyEnd[fyEnd] || 0) + n;
    };
    if (strategyOptions?.downsizeDetails?.giftingAmount) {
        addStrategyGift(strategyOptions.downsizeDetails.giftingAmount);
    }
    if (strategyOptions?.otherStrategies?.gifting && strategyOptions?.otherStrategies?.giftingAmount) {
        addStrategyGift(strategyOptions.otherStrategies.giftingAmount);
    }
    if (strategyOptions?.investmentPropertyDetails?.giftingAmount) {
        addStrategyGift(strategyOptions.investmentPropertyDetails.giftingAmount);
    }
    if (extraGiftsByFyEnd && typeof extraGiftsByFyEnd === 'object') {
        for (const [fy, amt] of Object.entries(extraGiftsByFyEnd)) {
            const n = amt || 0;
            if (n > 0) strategyGiftsByFyEnd[fy] = (strategyGiftsByFyEnd[fy] || 0) + n;
        }
    }

    return calculateDeprivedAssetsFromGifts(giftsHistory, {
        asOfDate,
        strategyGiftsByFyEnd,
    });
}
